import { useCallback, useEffect, useMemo, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useLoginUser } from "@hooks/useAuth";
import { toast } from "@/components/toast/CustomToast";
import { QUERY_KEYS } from "@/constants/QUERY_KEYS";
import { SearchInput } from "@/components/common/SearchInput";
import useAvailableMember from "@/hooks/useAvailbleMember";
import { AvailableMemberTable } from "./AvailableMemberTable";
import type {
  AvailableMember,
  AvailableMemberSearchBy,
} from "@/hooks/useAvailbleMember";
import httpClient from "@/config/axios";
import { useGroupSelection } from "@/hooks/useGroupSelection";
import NurseModalFooterButtons from "./NurseModalFooterButtons";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { ChevronDown, ChevronUp, X } from "lucide-react";
import LoadingSpiner from "@/components/ui/LoadingSpiner";

export default function NurseAddContents({
  onClose,
  selectedGroupId,
}: {
  onClose: () => void;
  selectedGroupId: string;
}) {
  const [isSelectionPanelOpen, setSelectionPanelOpen] = useState(true);
  const queryClient = useQueryClient();
  const loginUser = useLoginUser();

  const { groups } = useGroupSelection();
  const groupName =
    groups?.find((g) => g.group_id === selectedGroupId)?.group_name ||
    "선택된 그룹";

  const [availableSearchTerm, setAvailableSearchTerm] = useState("");
  const [availableSearchBy, setAvailableSearchBy] =
    useState<AvailableMemberSearchBy>("name");
  const [availableQuery, setAvailableQuery] = useState("");
  const [availableQuerySearchBy, setAvailableQuerySearchBy] =
    useState<AvailableMemberSearchBy>("name");

  // 현재 선택된 병동(group) 기준. 비-ADM(다병동 HN 포함)도 토큰 홈이 아니라
  // 화면에서 선택한 병동을 사용 — 저장 대상 group 과 일치시킨다.
  const availableMemberGroupId =
    selectedGroupId || loginUser?.group_id || "";

  const {
    data: availableMemberData,
    isError,
    error,
    isLoading: isAvailableLoading,
    isFetching: isAvailableFetching,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
  } = useAvailableMember({
    groupId: availableMemberGroupId,
    searchName: availableQuery,
    searchBy: availableQuerySearchBy,
  });

  useEffect(() => {
    if (isError) {
      console.error("메디통 병원 근무자 조회 오류:", error);
    }
  }, [isError, error]);

  const availableMemberList = useMemo(
    () => availableMemberData?.pages.flatMap((page) => page.items ?? []) ?? [],
    [availableMemberData]
  );

  const normalizedAvailableSearchTerm = availableSearchTerm.trim();
  const canLoadAvailableNextPage =
    normalizedAvailableSearchTerm === availableQuery &&
    availableSearchBy === availableQuerySearchBy &&
    Boolean(hasNextPage) &&
    !isFetchingNextPage;
  const isAvailableSearchLoading =
    availableQuery.length > 0 && isAvailableFetching && !isFetchingNextPage;

  const availableMemberById = useMemo(
    () =>
      new Map(
        availableMemberList.map((member) => [member.nurse_id, member] as const)
      ),
    [availableMemberList]
  );

  const [selectedMembers, setSelectedMembers] = useState<Set<string>>(
    new Set()
  );
  const [selectedMemberMap, setSelectedMemberMap] = useState<
    Record<string, AvailableMember>
  >({});

  const selectedMemberList = useMemo(
    () => Object.values(selectedMemberMap),
    [selectedMemberMap]
  );
  const selectedCount = selectedMemberList.length;

  const handleToggleSelection = (nurseId: string) => {
    // 타 병동 등록자는 선택 불가(병동이동 기능으로 분리).
    if (availableMemberById.get(nurseId)?.is_registered) return;
    setSelectedMembers((prev) => {
      const next = new Set(prev);
      const willSelect = !next.has(nurseId);
      if (willSelect) {
        next.add(nurseId);
      } else {
        next.delete(nurseId);
      }

      setSelectedMemberMap((map) => {
        const nextMap = { ...map };
        if (willSelect) {
          const member = availableMemberById.get(nurseId);
          if (member) nextMap[nurseId] = member;
        } else {
          delete nextMap[nurseId];
        }
        return nextMap;
      });

      return next;
    });
  };

  const handleRangeSelection = (nurseIds: string[]) => {
    // 범위 선택에서도 타 병동 등록자는 제외.
    const selectableIds = nurseIds.filter(
      (id) => !availableMemberById.get(id)?.is_registered
    );
    if (selectableIds.length === 0) return;
    setSelectedMembers((prev) => {
      const next = new Set(prev);
      selectableIds.forEach((id) => next.add(id));

      setSelectedMemberMap((map) => {
        const nextMap = { ...map };
        selectableIds.forEach((id) => {
          const member = availableMemberById.get(id);
          if (member) nextMap[id] = member;
        });
        return nextMap;
      });

      return next;
    });
  };

  const handleRemoveSelected = (nurseId: string) => {
    setSelectedMembers((prev) => {
      const next = new Set(prev);
      next.delete(nurseId);
      return next;
    });
    setSelectedMemberMap((prev) => {
      const next = { ...prev };
      delete next[nurseId];
      return next;
    });
  };

  const handleClearSelected = () => {
    setSelectedMembers(new Set());
    setSelectedMemberMap({});
  };

  const handleAvailableSearch = useCallback(() => {
    setAvailableQuery(availableSearchTerm.trim());
    setAvailableQuerySearchBy(availableSearchBy);
  }, [availableSearchBy, availableSearchTerm]);

  const handleSearchByChange = (value: string) => {
    const nextSearchBy = value as AvailableMemberSearchBy;
    setAvailableSearchBy(nextSearchBy);
    setAvailableSearchTerm("");
    setAvailableQuery("");
    setAvailableQuerySearchBy(nextSearchBy);
  };

  const handleAvailableSearchKeyDown = (
    e: React.KeyboardEvent<HTMLInputElement>
  ) => {
    if (e.key === "Enter") {
      handleAvailableSearch();
    }
  };

  const { mutate, isPending } = useMutation({
    mutationFn: async () => {
      const nurseIds = selectedMemberList.map((member) => member.nurse_id);
      // 현재 선택된 병동(group)의 nurses 에 저장. 비-ADM(다병동 HN)도 토큰 홈이 아니라
      // 선택 병동 기준 — 백엔드(resolve_effective_group)가 관리 권한을 검증한다.
      const targetGroupId = selectedGroupId || loginUser?.group_id || "";

      if (!targetGroupId) {
        throw new Error("그룹 ID가 없습니다.");
      }

      await httpClient.post("/nurses/add-to-group", {
        nurse_ids: nurseIds,
        group_id: targetGroupId,
      });
    },
    onSuccess: () => {
      toast.success("근무자 추가가 완료되었습니다.");
      // NURSES 베이스 키로 무효화(react-query prefix 매칭) → ADM/비-ADM 의 서로 다른
      // 목록 키([NURSES, selectedGroupId] / [NURSES, ""])를 모두 갱신.
      // (useSaveWeeklyOffNursesMutation 과 동일 패턴)
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.nurses.scope() });
      onClose();
    },
    onError: (err) => {
      // 조용한 실패 방지 — 권한(403)·그룹·서버 오류를 사용자에게 노출
      const detail =
        (err as { response?: { data?: { detail?: string } }; message?: string })
          ?.response?.data?.detail ||
        (err as { message?: string })?.message ||
        "근무자 추가에 실패했습니다.";
      toast.error(detail);
    },
  });

  const handleSaveClick = () => {
    if (selectedCount === 0) {
      toast.warning("추가할 근무자를 선택해주세요.");
      return;
    }
    if (isPending) return;
    // Radix Dialog 내부 버튼에서 바로 저장한다. (이전의 중첩 포털 확인모달은 Radix
    // Dialog 의 focus/pointer-events trap 에 가려 '예' 버튼이 클릭되지 않아 저장이
    // 아예 호출되지 않았다 → 제거). mutate 는 fire-and-forget — 오류는 onError 처리.
    mutate();
  };

  return (
    <>
      <div className="relative flex h-full min-h-[600px] flex-col bg-white font-pretendard">
        <div className="flex min-h-0 flex-1 flex-col">
          <header className="flex shrink-0 items-start justify-between border-b border-[#E5E7EB] px-6 pb-4 pr-14 pt-6">
            <div>
              <h2 className="text-[24px] font-bold text-sig">근무자 추가</h2>
              <p className="mt-1 text-[13px] text-[#6B7280]">{groupName}</p>
            </div>
            <div className="flex items-center gap-2 rounded-full border border-[#D6D6DE] bg-[#F8FAFC] px-4 py-2">
              <span className="text-[13px] text-[#6B7280]">선택됨</span>
              <strong className="text-[15px] text-[#0E59C6]">
                {selectedCount}명
              </strong>
            </div>
          </header>

          <section className="flex shrink-0 flex-wrap items-center justify-between gap-3 border-b border-[#E5E7EB] px-6 py-4">
            <SearchInput
              containerClassName="w-[368px]"
              placeholder={
                availableSearchBy === "name" ? "이름으로 검색" : "소속으로 검색"
              }
              value={availableSearchTerm}
              onChange={(e) => setAvailableSearchTerm(e.target.value)}
              onKeyDown={handleAvailableSearchKeyDown}
              onSearch={handleAvailableSearch}
              searchButtonDisabled={isAvailableSearchLoading}
              leading={
                <Select
                  value={availableSearchBy}
                  onValueChange={handleSearchByChange}
                >
                  <SelectTrigger className="h-full w-[86px] shrink-0 rounded-none border-0 bg-transparent px-4 text-sm shadow-none focus:ring-0 focus-visible:border-transparent focus-visible:ring-0">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent align="start" className="font-pretendard">
                    <SelectItem value="name">이름</SelectItem>
                    <SelectItem value="affiliation">소속</SelectItem>
                  </SelectContent>
                </Select>
              }
            />

            <button
              type="button"
              onClick={() => setSelectionPanelOpen((prev) => !prev)}
              className="inline-flex h-10 items-center gap-2 rounded-full border border-[#D6D6DE] bg-white px-4 text-[13px] font-semibold text-[#374151] hover:bg-[#F8FAFC]"
            >
              선택 목록 {isSelectionPanelOpen ? "접기" : "보기"}
              {isSelectionPanelOpen ? (
                <ChevronUp className="h-4 w-4" />
              ) : (
                <ChevronDown className="h-4 w-4" />
              )}
            </button>
          </section>

          {isSelectionPanelOpen && (
            <section className="shrink-0 border-b border-[#E5E7EB] bg-[#F8FAFC] px-6 py-3">
              <div className="mb-2 flex items-center justify-between">
                <span className="text-[13px] font-semibold text-[#374151]">
                  추가 예정 근무자 {selectedCount}명
                </span>
                {selectedCount > 0 && (
                  <button
                    type="button"
                    onClick={handleClearSelected}
                    className="text-[12px] font-semibold text-[#6B7280] hover:text-[#0E59C6]"
                  >
                    전체 해제
                  </button>
                )}
              </div>

              {selectedCount > 0 ? (
                <div className="flex max-h-[92px] flex-wrap gap-2 overflow-y-auto pr-1 scrollbar-float">
                  {selectedMemberList.map((member) => {
                    const affiliation =
                      member.small_kind_name ||
                      member.mb_part_name ||
                      member.middle_kind_name ||
                      member.big_kind_name ||
                      "-";
                    return (
                      <span
                        key={member.nurse_id}
                        className="inline-flex h-8 items-center gap-2 rounded-full border border-[#C7D2FE] bg-white px-3 text-[13px] text-[#1F2937]"
                      >
                        <span className="max-w-[180px] truncate">
                          {member.name || "-"} · {affiliation}
                        </span>
                        <button
                          type="button"
                          onClick={() => handleRemoveSelected(member.nurse_id)}
                          className="rounded-full text-[#9CA3AF] hover:text-[#0E59C6]"
                          aria-label={`${member.name || "근무자"} 선택 해제`}
                        >
                          <X className="h-3.5 w-3.5" />
                        </button>
                      </span>
                    );
                  })}
                </div>
              ) : (
                <div className="flex h-10 items-center text-[13px] text-[#6B7280]">
                  병원 근무자 목록에서 추가할 근무자를 선택하세요.
                </div>
              )}
            </section>
          )}

          <main className="min-h-[380px] flex-1 px-6 py-4">
            <div className="mb-2 flex items-center">
              <h3 className="text-[15px] font-bold text-[#111827]">
                병원 근무자
              </h3>
            </div>

            <AvailableMemberTable
              key={`${availableMemberGroupId}-${availableQuerySearchBy}-${availableQuery}`}
              members={availableMemberList}
              selectedMembers={selectedMembers}
              onToggleSelection={handleToggleSelection}
              onRangeSelection={handleRangeSelection}
              hasNextPage={canLoadAvailableNextPage}
              isLoading={isAvailableLoading}
              isSearching={isAvailableSearchLoading}
              isFetchingNextPage={isFetchingNextPage}
              searchTerm={availableQuery}
              searchBy={availableQuerySearchBy}
              onLoadMore={() => {
                if (!canLoadAvailableNextPage) return;
                void fetchNextPage();
              }}
              className="h-[calc(100%-28px)] min-h-[340px] overflow-y-auto scrollbar-float"
            />
          </main>
        </div>

        <NurseModalFooterButtons
          onClose={onClose}
          onConfirm={handleSaveClick}
          confirmLabel={isPending ? "저장 중..." : `선택한 ${selectedCount}명 저장`}
          confirmDisabled={selectedCount === 0 || isPending}
          className="px-6 pb-5 pt-1 before:-top-6 before:h-6"
        />
        {isPending && (
          <div className="absolute inset-0 z-20 flex items-center justify-center bg-white/60 backdrop-blur-[1px]">
            <div className="rounded-lg bg-white px-10 py-5 shadow-2xl">
              <LoadingSpiner msg="근무자를 추가하는 중입니다" />
            </div>
          </div>
        )}
      </div>
    </>
  );
}
